import { useTranslations } from "contexts/LocalizationContext/hooks";
import { useModal } from "contexts/ModalContext";
import { TrustpilotSummary } from "modules/common/components/Trustpilot";
import isEmpty from "modules/storyblok/adapters/RichtextAdapter/utils/isEmpty";
import { Grid } from "modules/common/components/Grid";
import { trackUAEvent, trackCustomEvent } from "utils/tracking";
import RichtextAdapter from "modules/storyblok/adapters/RichtextAdapter";
import { contentStyles } from "modules/storyblok/adapters/Content";
import { Box, Button, Image, Flex, useToken, useMediaQuery } from "@chakra-ui/react";
import { Modal } from "../../Modal";
import { HeroCard } from "./HeroCard";
import type { HeroProps } from "./Hero.d";

const SHARE_NOW_HERO_GTM = ["clicked", "Hero", "Share_now"] as const;
const MODAL_GENERATE_LINK_EVENT = { event: "help_friend", label: "share_now", placement: "hero" } as const;

export const Hero = ({ title, content, buttonText, commissionValue, imgSrc }: HeroProps) => {
  const translations = useTranslations();
  const { openModal } = useModal();
  const [isLessThan480] = useMediaQuery("(max-width: 480px)");
  const [primary900, white] = useToken("colors", ["primary.900", "white"]);
  const [lg, xl5] = useToken("fontSizes", ["lg", "5xl"]);

  const clickHandler = () => {
    openModal(<Modal />, "lg");
    trackUAEvent(SHARE_NOW_HERO_GTM);
    trackCustomEvent(MODAL_GENERATE_LINK_EVENT);
  };

  const svgContentConfig = [
    {
      id: 1,
      position: { fromLeft: 305, fromTop: 93 },
      styles: { fill: primary900, fontSize: xl5, fontWeight: "bold" },
      value: `€${commissionValue}`,
    },
    {
      id: 2,
      position: { fromLeft: 324, fromTop: 218 },
      styles: { fill: primary900, fontSize: lg, fontWeight: "bold", letterSpacing: "2px" },
      value: `12/${(new Date().getFullYear() + 3).toString().substring(2)}`,
    },
    {
      id: 3,
      position: { fromLeft: 71, fromTop: 218 },
      styles: { fill: white, fontSize: lg, fontWeight: "bold", letterSpacing: "2px" },
      value: "Michelle Smith",
    },
  ];

  return (
    <Grid mt={{ base: 32, lg: 40 }} mb={{ base: isLessThan480 ? 32 : 16, md: 32 }} mx="auto" overflowX="clip">
      <Box gridColumn={{ base: "initial", md: "span 6", lg: "span 5" }} alignSelf="center" mr={{ md: 2, lg: 0 }}>
        <TrustpilotSummary />
        {!isEmpty(title) && (
          <RichtextAdapter
            content={title}
            customStyles={{
              ...contentStyles,
              heading: {
                variant: "h1",
                as: "h1",
                my: 4,
              },
            }}
          />
        )}
        {!isEmpty(content) && (
          <RichtextAdapter content={content} customStyles={{ ...contentStyles, paragraph: { mb: 4 } }} />
        )}
        <Button
          data-testid="hero-share-now"
          px={10}
          width={{ base: "100%", md: "fit-content" }}
          my={4}
          mr={{ base: 0, md: 5 }}
          onClick={clickHandler}
          title={buttonText || translations("button_share_now")}
        >
          {buttonText || translations("button_share_now")}
        </Button>
      </Box>
      <Flex
        gridColumn={{ base: "initial", md: "span 6", lg: "7 / span 6" }}
        alignSelf={{ base: "center", lg: "flex-end" }}
        justify="center"
        align="center"
      >
        <Box pos="relative">
          <Image objectFit="cover" borderRadius="xl" src={imgSrc} alt={translations("help_a_friend_image_alt")} />
          <Box
            pos="absolute"
            transform={{ base: "translateX(-6%)", md: "translateX(-16%)", lg: "translateX(-27%)" }}
            top={{ base: "51%", md: "20%", lg: "20%" }}
          >
            <HeroCard content={svgContentConfig} />
          </Box>
        </Box>
      </Flex>
    </Grid>
  );
};
